import { ArrowLeft, ArrowUpFromLine, CalendarCheck, Dumbbell, Mail, MapPin, Printer, ShieldCheck, Users, Zap } from "lucide-react";
import { LogoMark } from "../components/Logo";

const SKILLS = [
    { icon: Dumbbell, title: "Get stronger and steadier", text: "Leg strength and balance that help prevent falls in the first place." },
    { icon: Zap, title: "Catch yourself", text: "Quick-reaction and stepping skills for when you trip or slip." },
    { icon: ShieldCheck, title: "Land more safely", text: "Protective habits for your head, hips and wrists, learned gradually on padded mats." },
    { icon: ArrowUpFromLine, title: "Get back up", text: "How to get up from the floor safely, and what to do if you can't." },
];

export default function Flyer() {
    return (
        <div className="min-h-screen bg-sand px-4 py-8 print:bg-white print:p-0">
            <div className="mx-auto max-w-[8.5in]">
                <div className="no-print mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <a
                        href="/"
                        data-testid="flyer-back-link"
                        className="inline-flex h-12 items-center gap-2 self-start rounded-full border-2 border-ink px-5 text-base font-semibold text-ink transition-colors duration-200 hover:bg-ink hover:text-cream"
                    >
                        <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                        Back to the site
                    </a>
                    <button type="button" onClick={() => window.print()} data-testid="flyer-print-button" className="btn-primary self-start">
                        <Printer className="h-5 w-5" aria-hidden="true" />
                        Print flyer
                    </button>
                </div>
                <div className="flyer-sheet border border-clay bg-white p-9 shadow-sm print:border-0 print:p-0 print:shadow-none" data-testid="flyer-sheet">
                    <div className="flex items-start justify-between gap-6 border-b-2 border-ink pb-5">
                        <div className="flex items-center gap-3">
                            <LogoMark className="h-12 w-12" />
                            <div>
                                <p className="font-display text-2xl font-black tracking-tight text-ink">Fall Safe Ready</p>
                                <p className="text-base font-semibold text-teal-800">Ready to fall safely. Get up confidently.</p>
                            </div>
                        </div>
                        <p className="hidden text-right text-sm font-bold uppercase tracking-wide text-ink-soft sm:block">
                            Small-group training
                            <br />
                            for active adults 55+
                        </p>
                    </div>

                    <p className="mt-5 text-lg leading-relaxed text-ink">
                        <strong>Fall Safe Ready</strong> teaches adults 55+ what to do before, during and after a
                        fall — at your own pace, on padded mats, in small groups. First workshops in early 2027
                        in Arlington, Virginia.
                    </p>

                    <h2 className="mt-6 font-display text-lg font-extrabold uppercase tracking-wide text-teal-800">
                        What you&rsquo;ll learn
                    </h2>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        {SKILLS.map((s) => (
                            <div key={s.title} className="flex gap-3 rounded-2xl border border-clay p-4">
                                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-teal-50 text-teal-700" aria-hidden="true">
                                    <s.icon className="h-5 w-5" />
                                </span>
                                <div>
                                    <p className="font-display text-base font-extrabold leading-tight text-ink">{s.title}</p>
                                    <p className="mt-1 text-base leading-snug text-ink-soft">{s.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-5 flex items-start gap-4 rounded-2xl border border-teal-600/25 bg-teal-50 p-5">
                        <CalendarCheck className="h-7 w-7 shrink-0 text-teal-700" aria-hidden="true" />
                        <p className="text-base leading-relaxed text-ink">
                            <strong>How it starts:</strong> a one-hour introductory workshop with no falling
                            practice, then — if you&rsquo;d like — the full course: eight sessions over four
                            weeks. Simple before-and-after checks show your progress.
                        </p>
                    </div>

                    <div className="mt-3 flex items-start gap-4 rounded-2xl border border-clay bg-sand p-5">
                        <Users className="h-7 w-7 shrink-0 text-teal-700" aria-hidden="true" />
                        <p className="text-base leading-relaxed text-ink">
                            <strong>Bring it to your organization:</strong> hosting is easy — a workshop needs
                            only a room, sturdy chairs and some open floor space. Every participant completes a
                            health questionnaire, and anyone better served by Otago or A Matter of Balance is
                            referred there.
                        </p>
                    </div>

                    <div className="mt-5 rounded-2xl bg-ink p-5 text-cream">
                        <p className="font-display text-lg font-extrabold">Join the interest list</p>
                        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-base">
                            <span className="inline-flex items-center gap-2">
                                <Mail className="h-4 w-4" aria-hidden="true" />
                                SarahGmyr@gmail.com
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <MapPin className="h-4 w-4" aria-hidden="true" />
                                Arlington, Virginia
                            </span>
                            <span>fallsaferready.preview.emergentagent.com</span>
                        </div>
                    </div>

                    <p className="mt-5 text-sm leading-relaxed text-ink-soft">
                        Fall Safe Ready classes are educational and are not medical treatment. Talk with your
                        doctor before starting a new exercise program. Fall Safe Ready is an independent program
                        and is not affiliated with or endorsed by Otago, A Matter of Balance or SAIL. © 2026 Fall
                        Safe Ready.
                    </p>
                </div>
            </div>
        </div>
    );
}